/**
 * Was neben dem Brett steht — §49, abgeleitet aus dem Partiezustand.
 *
 * §49 zählt auf, was außer dem Brett sichtbar sein muss: Spieler, Farben, Teams, Punktestand,
 * **aktive Person**, Bedenkzeiten, Respawn-Pools und der Spielstatus. Diese Schicht stellt das
 * zusammen; sie rechnet keine Regel nach.
 *
 * **Punkte liegen je Spieler, Seiten sind eine Summe** (§23). Beides steht hier, weil §23
 * ausdrücklich verlangt, die individuellen Punkte nicht unmöglich zu machen — und weil ein Team
 * trotzdem seinen Stand sehen will. Summiert wird über `sideScore` aus der Engine, nicht über
 * eine eigene Schleife: dann können die beiden Zahlen nicht auseinanderlaufen.
 *
 * **Die Reihenfolge ist die Zugreihenfolge** (§9) und nicht die Spielerliste. Wer als Nächstes
 * zieht, soll ablesbar sein und nicht gesucht werden müssen.
 *
 * **Die Uhr kommt aus der `CLOCK`-Nachricht, nicht aus `state.timer`.** Beides stammt
 * serverseitig aus demselben `TimerState`, aber nur die Nachricht trägt den **Serverzeitpunkt**
 * `at`, zu dem die Werte galten — und ohne den kann der Client nicht weiterzählen, ohne zu raten.
 * Zwei Quellen für dieselbe Zahl wären genau die Art Doppelung, die still falsch wird; deshalb
 * liest diese Schicht `state.timer` gar nicht.
 *
 * **Weitergezählt wird mit einer verstrichenen Spanne, nicht mit einer Uhrzeit.** Der Aufrufer
 * reicht herein, wie lange die `CLOCK`-Nachricht her ist (lokal gemessen). Damit hängt die
 * Anzeige nicht davon ab, ob Browser- und Serveruhr übereinstimmen — ein Versatz von Minuten ist
 * real (Befund aus Zyklus 10f: 898 Sekunden zwischen Container- und Prozessuhr).
 *
 * **Die Anzeige ist ein Hinweis, keine Autorität.** Über den Zeitablauf entscheidet der Server
 * (`session.tick`, §38); hier wird nur dargestellt, und deshalb wird auch nichts ausgelöst.
 */

import {
  capturedOf,
  isEliminated,
  sideOf,
  sideScore,
  type CapturedPiece,
  type GameOutcome,
  type GameState,
  type GameStatus,
  type SectorId,
} from '@flaghex/engine';

import type { LobbyBotDto } from '@flaghex/contracts';

import type { ClockView } from '../realtime/view.js';
import type { FallbackSummary } from './history.js';

export interface PlayerStatus {
  readonly id: string;
  /** Aus der Lobby-Ansicht; fehlt sie, bleibt die Kennung stehen. */
  readonly name: string;
  readonly sector: SectorId;
  readonly team: string | undefined;
  /** Die Seite: das Team, oder der Spieler selbst (§26). */
  readonly side: string;
  readonly score: number;
  readonly sideScore: number;
  readonly active: boolean;
  readonly connected: boolean;
  /**
   * Steuert ein Bot diesen Platz? (§3, **E53** — Abzeichen seit `V2-2f2`, **E64**)
   *
   * **Gelesen, nicht erfunden**: die Angabe steht am Spieler im Zustand. Daraus folgen zwei
   * Dinge in der Anzeige — das Abzeichen, und dass die **Verbindungsanzeige entfällt**:
   * `connected` ist für einen Bot immer `false`, und „nicht verbunden" stand in der
   * Sichtprüfung von `V2-2e3` auf dem Bildschirm, **während er zog**.
   */
  readonly bot: boolean;
  /**
   * **Welcher** Bot (`L3a`, **E104** Teilfrage 3) — die Positivliste aus der Lobby-Ansicht, wie
   * die Namen. Nur gesetzt, wenn `bot` es ist: das Bot-Sein kommt aus dem Zustand, nicht von hier.
   */
  readonly botDescription: LobbyBotDto | undefined;
  /** Wie oft dieser Bot auf `greedy` zurückgefallen ist (`L3b`, **E112** Antwort 2). */
  readonly botFallbacks: FallbackSummary | undefined;
  readonly eliminated: boolean;
  /** Die geschlagenen Figuren dieses Spielers (§22, §49) — ab `PLAYING` öffentlich. */
  readonly pool: readonly CapturedPiece[];
  /** Verbleibende Bedenkzeit. `undefined` in Variante A — dort gibt es keine Uhr (Nr. 22). */
  readonly remainingMs: number | undefined;
  readonly clockRunning: boolean;
}

export interface StatusView {
  readonly players: readonly PlayerStatus[];
  /** Gibt es Teams? Nur dann ist die Seitenzahl eine Aussage neben der Einzelzahl. */
  readonly teams: boolean;
  readonly status: GameStatus;
  readonly turnNumber: number;
  readonly outcome: GameOutcome | undefined;
  /** Nur in Variante A — in Variante B ist das Punktziel wirkungslos (Nr. 22). */
  readonly pointTarget: number | undefined;
  /**
   * Ist diese Partie **regelwidrig erzeugt** worden? (Nr. 38, **E39** — Zyklus 11g)
   *
   * Daraus wird das Abzeichen am Statusbereich. Es gehört der **Partie** und keiner Zeile: in
   * einer Spielerzeile sähe es aus, als hätte dieser Spieler etwas getan.
   */
  readonly ruleOverridden: boolean;
}

export interface StatusInput {
  readonly state: GameState;
  readonly names: ReadonlyMap<string, string>;
  /**
   * Was die Lobby-Ansicht über jeden Bot sagt (`L3a`) — **Pflicht**, wie `ruleOverridden`: eine
   * vergessene Angabe fiele nur als „Bot" ohne Strategie auf, und das sieht nach Absicht aus.
   */
  readonly bots: ReadonlyMap<string, LobbyBotDto>;
  /** Die Rückfälle je Spieler, aus der Historie (`L3b`) — Pflicht aus demselben Grund. */
  readonly fallbacks: ReadonlyMap<string, FallbackSummary>;
  readonly connected: ReadonlySet<string>;
  readonly clock: ClockView | undefined;
  /** Wie lange die `CLOCK`-Nachricht her ist, lokal gemessen. Siehe Modulkopf. */
  readonly elapsedSinceClockMs: number;
  /**
   * Die Herkunftsmarkierung aus der Zustandsnachricht (Nr. 38, Zyklus 11g).
   *
   * **Pflichtangabe, obwohl `false` der Normalfall ist.** Optional wäre sie die eine Angabe,
   * die ein Aufrufer vergisst — und ein fehlendes Abzeichen fällt niemandem auf. Genau das
   * schließt Nr. 38 aus („nie stillschweigend").
   */
  readonly ruleOverridden: boolean;
}

/**
 * Eine Restzeit als `m:ss`.
 *
 * An **einer** Stelle, weil sie an zweien erscheint: an der Schachuhr und an der
 * Aufstellungsfrist. Zwei Formatierungen derselben Größe wären zwei Schreibweisen für dasselbe.
 * Null oder negativ heißt abgelaufen — die Anzeige geht nie unter `0:00`.
 *
 * **Aufgerundet, nicht abgerundet.** Eine Restzeit von 125 000 ms ist „2:05", und zwar solange,
 * bis wirklich eine Sekunde vergangen ist. Abgerundet sprang die Anzeige schon nach zwei
 * Millisekunden auf 2:04 — gefunden von einem Test, der die Uhr beim ersten Zeichnen abliest.
 * So verhält sich auch eine Schachuhr: sie zeigt an, wie viel **noch bleibt**.
 */
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export function statusOf({
  state,
  names,
  bots,
  fallbacks,
  connected,
  clock,
  elapsedSinceClockMs,
  ruleOverridden,
}: StatusInput): StatusView {
  const byId = new Map(state.players.map((player) => [player.id, player]));

  const players = state.turnOrder.flatMap((id): PlayerStatus[] => {
    const player = byId.get(id);
    if (player === undefined) return [];

    const stored = clock?.remaining[id];
    const running = clock?.running === id;
    const remainingMs =
      stored === undefined
        ? undefined
        : running
          ? Math.max(0, stored - elapsedSinceClockMs)
          : stored;

    return [
      {
        id,
        name: names.get(id) ?? id,
        sector: player.sector,
        team: player.team,
        side: sideOf(state, id),
        score: state.scores.get(id) ?? 0,
        // `sideScore` nimmt die **Spieler**-ID und sucht die Seite selbst — eine Seiten-ID
        // hineinzugeben wirft (einmal passiert, vom Test gefangen).
        sideScore: sideScore(state, id),
        // **Am Zug ist nur jemand, solange gespielt wird.** `activePlayer` bleibt nach dem
        // Partieende stehen — der Zustand wird eingefroren, nicht geleert —, und die Zeile sagte
        // deshalb „am Zug", während darunter schon der Sieger stand (Sichtprüfung zu Zyklus 10i).
        // Dieselbe Bedingung wie in `turn.ts`: wer am Zug ist, ist an **einer** Stelle definiert.
        active: state.status === 'PLAYING' && state.activePlayer === id,
        connected: connected.has(id),
        // Aus dem Zustand gelesen — ein festes `false` machte einen Bot stillschweigend zum
        // Menschen, und sichtbar wäre davon nur ein **fehlendes** Abzeichen.
        bot: player.playerType === 'BOT',
        botDescription: player.playerType === 'BOT' ? bots.get(id) : undefined,
        botFallbacks: player.playerType === 'BOT' ? fallbacks.get(id) : undefined,
        eliminated: isEliminated(state, id),
        pool: capturedOf(state.boardState, id),
        remainingMs,
        clockRunning: running,
      },
    ];
  });

  return {
    players,
    teams: state.players.some((player) => player.team !== undefined),
    status: state.status,
    turnNumber: state.turnNumber,
    outcome: state.outcome,
    // In Variante B ist der Wert im Snapshot zwar vorhanden, aber wirkungslos — ihn trotzdem
    // anzuzeigen wäre eine Zahl, auf die niemand spielt.
    pointTarget: state.victory === 'POINTS' ? state.ruleConfig.scoring.target : undefined,
    ruleOverridden,
  };
}
